import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { ConnectorMonthCard, ConnectorMonth } from './ApiKeysPanel';

interface SubscriptionState {
  userId: number;
  source: 'tiered' | 'grandfathered' | 'none';
  status?: string | null;
  // Latest payment failed (Stripe past_due / unpaid): plan shown, no allowance until paid.
  paymentDue?: boolean;
  // Last plan was a Starter/Growth/Scale plan that's no longer live.
  endedPlan?: { name: string | null; endedAt: string | null } | null;
  // Set while inside the 7-day free trial; endsAt = trial end / first charge.
  trial?: { endsAt: string | null } | null;
  // Enterprise accounts: cost-price add-on rates (pence; may be fractional).
  // Any rate may be null = the normal price (migration 216).
  // Prepaid / enterprise plan end date (null = no end date).
  prepaidEndsAt?: string | null;
  enterprise?: { monitoringPencePerCheck: number | null; contactPencePerCredit: number | null; jobsBundlePence?: number | null } | null;
  // Seats (migration 197): maxSeats null = unlimited.
  seats?: { used: number; pending: number; maxSeats: number | null; plan: string | null; upgradeTo: string | null } | null;
  tier: { id: number; key: string; displayName: string; monthlyPricePence: number } | null;
  legacyPlan: {
    type: 'monitoring' | 'pro' | 'access';
    label: string;
    monthlyPricePence: number | null;
  } | null;
  baseQuota: { monitoring: number; jobs: number };
  activeBoltOns: Array<{
    id?: number;
    type: 'monitoring' | 'jobs';
    quantity: number;
    quotaPerUnit: number;
    unitPricePence: number;
    scheduledRemovalAt: string | null;
  }>;
  totalQuota: { monitoring: number; jobs: number };
  boltOnRates: {
    monitoring: { quotaPerUnit: number; unitPricePence: number } | null;
    jobs: { quotaPerUnit: number; unitPricePence: number } | null;
  };
  cycle: {
    used: number;
    periodStart: string | null;
    periodEnd: string | null;
    dailyQuotaOverride: number;
    cancelAtPeriodEnd: boolean;
  };
  // Minimum-term commitment (the /offer flow). Non-null while the user has
  // a recorded commitment; `active` is true until `end` passes.
  commitment: {
    termMonths: number;
    start?: string | null;
    end: string;
    active: boolean;
    originalTierKey: string | null;
    originalPricePence: number | null;
  } | null;
}

interface Tier {
  id: number;
  key: string;
  display_name: string;
  monthly_price_pence: number;
  monthly_monitoring_checks: number;
  monthly_job_vacancies: number;
  monthly_contact_credits: number;
  includes_ada: boolean;
  display_order: number;
  max_seats?: number | null;
  bolt_ons: {
    monitoring?: { quota_per_unit: number; unit_price_pence: number };
    jobs?: { quota_per_unit: number; unit_price_pence: number };
  };
}

const formatGBP = (pence: number) => `£${(pence / 100).toFixed(0)}`;
// Pence-exact, for cost-price enterprise amounts (e.g. £135, £25.50).
const formatGBPExact = (pence: number) =>
  `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;

// "1–4 users" / "5–14 users" / "15+ users": each tier starts one above the
// previous tier's limit.
const seatRange = (tiers: Tier[], tier: Tier) => {
  const ordered = [...tiers].sort((a, b) => a.display_order - b.display_order);
  const i = ordered.findIndex((t) => t.key === tier.key);
  const prevMax = i > 0 ? ordered[i - 1].max_seats : 0;
  const from = (prevMax || 0) + 1;
  void from;
  // "Up to N users included" — read as one subscription for the team, not
  // a number of subscriptions (client feedback on "5–14 users").
  if (tier.max_seats == null) return 'Unlimited users included';
  return `Up to ${tier.max_seats} users included`;
};
const formatNumber = (n: number) => n.toLocaleString('en-GB');

interface Props {
  onError?: (msg: string) => void;
}

const SubscriptionPanel: React.FC<Props> = ({ onError }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [state, setState] = useState<SubscriptionState | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  // Starter's free month of Claude (offer / running / ended) — the same
  // card as Settings → Integrations, here because this is where people look.
  const [connector, setConnector] = useState<ConnectorMonth | null>(null);
  const loadConnector = useCallback(async () => {
    try {
      const r = await fetch('/api/api-keys/connector-month', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setConnector(await r.json());
    } catch { /* card stays hidden */ }
  }, [token]);

  const reportError = (msg: string) => {
    setLocalError(msg);
    if (onError) onError(msg);
  };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [stateRes, tiersRes] = await Promise.all([
        fetch('/api/subscription/state', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/subscription/tiers', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (!stateRes.ok || !tiersRes.ok) throw new Error('Failed to load subscription data');
      const stateData = await stateRes.json();
      const tiersData = await tiersRes.json();
      setState(stateData);
      setTiers(tiersData.tiers || []);
    } catch (err: any) {
      reportError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { loadConnector(); }, [loadConnector]);

  const handleManageLegacy = async () => {
    try {
      setActionLoading(true);
      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Failed to open subscription management');
      }
    } catch {
      reportError('Failed to open subscription management');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChoosePlan = async (tierKey: string) => {
    try {
      setActionLoading(true);
      setLocalError(null);
      const response = await fetch('/api/subscription/checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tierKey })
      });
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe-hosted checkout. After payment Stripe redirects
        // back to /settings?tab=subscription&success=true.
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Failed to start checkout');
      }
    } catch {
      reportError('Failed to start checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddBoltOn = async (type: 'monitoring' | 'jobs') => {
    const rate = state?.boltOnRates[type];
    if (!rate) return;
    const quotaLabel = type === 'monitoring' ? 'monitoring checks' : 'tracked jobs';
    const confirmed = window.confirm(state?.enterprise
      // Enterprise: the add-on is its own monthly card subscription.
      ? `Add +${formatNumber(rate.quotaPerUnit)} ${quotaLabel} for ${formatGBPExact(rate.unitPricePence)}/mo?\n\n` +
        `You'll be taken to a secure Stripe checkout. It's billed monthly to your card until you remove it.`
      : `Add +${formatNumber(rate.quotaPerUnit)} ${quotaLabel} for ${formatGBP(rate.unitPricePence)}/mo?\n\n` +
        `You'll be charged a prorated amount immediately for the remainder of your current billing period, ` +
        `then ${formatGBP(rate.unitPricePence)}/mo from your next renewal.`
    );
    if (!confirmed) return;
    try {
      setActionLoading(true);
      setLocalError(null);
      const response = await fetch('/api/subscription/bolt-ons/add', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, quantity: 1 })
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.url) { window.location.href = data.url; return; } // enterprise checkout
        await fetchAll();
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Failed to add add-on');
      }
    } catch {
      reportError('Failed to add add-on');
    } finally {
      setActionLoading(false);
    }
  };

  // Result of a plan switch, shown right above the plan cards (the panel's
  // general error banner sits at the top, often scrolled out of view).
  const [tierNotice, setTierNotice] = useState<{ ok: boolean; msg: string } | null>(null);

  const handleSwitchTier = async (newTierKey: string) => {
    const newTier = tiers.find(t => t.key === newTierKey);
    const currentTier = state?.tier;
    if (!newTier || !currentTier) return;
    const isUpgrade = newTier.monthly_price_pence > currentTier.monthlyPricePence;
    const direction = isUpgrade ? 'Upgrade' : 'Downgrade';
    const confirmed = window.confirm(
      `${direction} from ${currentTier.displayName} (${formatGBP(currentTier.monthlyPricePence)}/mo) ` +
      `to ${newTier.display_name} (${formatGBP(newTier.monthly_price_pence)}/mo)?\n\n` +
      (isUpgrade
        ? 'You\'ll be charged the prorated difference immediately for the rest of your billing period. ' +
          'Any active add-ons stay attached and switch to the new tier\'s rates from the next renewal.'
        : 'You\'ll receive a prorated credit applied to your next invoice. ' +
          'Any active add-ons stay attached and switch to the new (higher) tier rates from the next renewal — ' +
          'check the new add-on prices before confirming.')
    );
    if (!confirmed) return;
    try {
      setActionLoading(true);
      setLocalError(null);
      setTierNotice(null);
      const response = await fetch('/api/subscription/change-tier', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tierKey: newTierKey })
      });
      if (response.ok) {
        await fetchAll();
        setTierNotice({ ok: true, msg: `Done — you're now on the ${newTier.display_name} plan.` });
      } else {
        const errorData = await response.json().catch(() => ({}));
        const msg = errorData.error || 'Failed to switch plan — please try again.';
        reportError(msg);
        setTierNotice({ ok: false, msg });
      }
    } catch {
      reportError('Failed to switch plan — please try again.');
      setTierNotice({ ok: false, msg: 'Failed to switch plan — please try again.' });
    } finally {
      setActionLoading(false);
    }
  };

  // In-trial actions. The sign-up terms promise cancel-at-no-cost during
  // the trial, and activating early starts billing (and the 12-month
  // term) today.
  const trialAction = async (path: string, confirmMsg: string | null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      setActionLoading(true);
      setLocalError(null);
      const response = await fetch(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await fetchAll();
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Something went wrong — please try again.');
      }
    } catch {
      reportError('Something went wrong — please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const trialEndStr = state?.trial?.endsAt
    ? new Date(state.trial.endsAt).toLocaleDateString('en-GB')
    : 'the end of your trial';

  const handleCancelTrial = () => trialAction(
    '/api/subscription/trial/cancel',
    `Cancel your free trial?\n\n` +
    `You'll keep full access until ${trialEndStr}, and nothing will be charged. ` +
    `You can change your mind any time before then.`
  );

  const handleResumeTrial = () => trialAction('/api/subscription/trial/resume', null);

  const handleActivateNow = () => trialAction(
    '/api/subscription/trial/activate',
    `Start your paid plan now?\n\n` +
    `Your free trial ends immediately: the first payment of ` +
    `${state?.tier ? formatGBP(state.tier.monthlyPricePence) : 'your plan price'} is taken today, ` +
    `and your 12-month term starts today instead of at the trial's end.`
  );

  const handleCancel = async () => {
    const periodEnd = state?.cycle.periodEnd;
    const dateStr = periodEnd ? new Date(periodEnd).toLocaleDateString('en-GB') : 'the end of your billing period';
    const confirmed = window.confirm(
      `Cancel your subscription?\n\n` +
      `You'll keep full access until ${dateStr}, and won't be charged again. ` +
      `You can re-subscribe at any time before then to undo.`
    );
    if (!confirmed) return;
    // Tiered (new pricing) subscriptions are cancelled via the
    // /api/subscription/cancel endpoint, which filters on tier_id IS NOT
    // NULL in the DB. Legacy grandfathered plans (Pro £100/mo, legacy
    // monitoring tiers) don't have a tier_id, so they cancel through the
    // Stripe-direct legacy endpoint, which scans the customer's Stripe
    // subscriptions for known legacy product / price IDs.
    const cancelUrl = state?.source === 'grandfathered'
      ? '/api/stripe/cancel-subscription'
      : '/api/subscription/cancel';
    try {
      setActionLoading(true);
      setLocalError(null);
      const response = await fetch(cancelUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await fetchAll();
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Failed to cancel subscription');
      }
    } catch {
      reportError('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleRemoval = async (boltOnId?: number) => {
    if (!boltOnId) return;
    if (!window.confirm('Remove this add-on at the end of your current billing period? You\'ll keep it until then.')) return;
    try {
      setActionLoading(true);
      setLocalError(null);
      const response = await fetch(`/api/subscription/bolt-ons/${boltOnId}/schedule-removal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        await fetchAll();
      } else {
        const errorData = await response.json();
        reportError(errorData.error || 'Failed to schedule removal');
      }
    } catch {
      reportError('Failed to schedule removal');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading subscription details...</p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
        {localError || 'Could not load subscription'}
      </div>
    );
  }

  const monitoringPct = state.totalQuota.monitoring > 0
    ? Math.min(100, Math.round((state.cycle.used / state.totalQuota.monitoring) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {localError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
          <p className="text-red-800 text-sm">{localError}</p>
          <button onClick={() => setLocalError(null)} className="text-red-600 text-sm underline">Dismiss</button>
        </div>
      )}

      {/* Header row: tier badge / price */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600">Current plan</p>
          {state.source === 'tiered' && state.tier ? (
            <div className="mt-1">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {state.tier.displayName} Plan
                </div>
                {state.enterprise && (
                  <div className="inline-block px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Enterprise
                  </div>
                )}
                {state.trial && (
                  <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Free trial
                  </div>
                )}
                {state.cycle.cancelAtPeriodEnd && (
                  <div className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium uppercase tracking-wide">
                    Cancelling
                  </div>
                )}
              </div>
              {state.enterprise ? (
                <div>
                  <div className="text-2xl font-bold text-gray-900">Included</div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Full access, no monthly fee{state.prepaidEndsAt ? `, until ${new Date(state.prepaidEndsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}.{' '}
                    {state.baseQuota.monitoring > 0
                      ? `${formatNumber(state.baseQuota.monitoring)} monitoring checks a month included; more by add-on below.`
                      : 'Monitoring and job vacancy tracking are by add-on, priced below.'}{' '}
                    {state.enterprise.contactPencePerCredit != null
                      ? `Contact data £${(state.enterprise.contactPencePerCredit / 100).toFixed(3)} per credit.`
                      : 'Contact credits at the standard prices.'}
                  </p>
                </div>
              ) : (
                <div className="text-2xl font-bold text-gray-900">
                  {formatGBP(state.tier.monthlyPricePence)}<span className="text-base font-normal text-gray-500">/mo</span>
                </div>
              )}
            </div>
          ) : state.source === 'grandfathered' && state.legacyPlan ? (
            <div className="mt-1">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="inline-block px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                  {state.legacyPlan.label}
                </div>
                {state.cycle.cancelAtPeriodEnd && (
                  <div className="inline-block px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium uppercase tracking-wide">
                    Cancelling
                  </div>
                )}
              </div>
              {state.legacyPlan.monthlyPricePence != null && state.legacyPlan.monthlyPricePence > 0 && (
                <div className="text-2xl font-bold text-gray-900">
                  {formatGBP(state.legacyPlan.monthlyPricePence)}
                  <span className="text-base font-normal text-gray-500">/mo</span>
                </div>
              )}
            </div>
          ) : state.endedPlan ? (
            <div className="mt-1">
              <div className="inline-block px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
                {state.endedPlan.name ? `${state.endedPlan.name} plan ended` : 'Plan ended'}
              </div>
              <p className="text-sm text-gray-500 mt-2">Choose a plan below to pick up where you left off — your projects are still here.</p>
            </div>
          ) : (
            <div className="mt-1 inline-block px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm font-medium">
              No Active Subscription
            </div>
          )}
        </div>
      </div>

      {state.paymentDue && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-900">
            Your last payment{state.tier ? ` for the ${state.tier.displayName} plan` : ''} didn't go through.
          </p>
          <p className="text-xs text-red-800 mt-1">
            Update your card and pay the open invoice to restore your plan's allowances.
          </p>
          <button
            onClick={handleManageLegacy}
            disabled={actionLoading}
            className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            Update card &amp; pay
          </button>
        </div>
      )}

      {/* 7-day free trial: say exactly where they stand and give both
          exits — convert now, or cancel at no cost. The 12-month term
          only starts when the trial ends. */}
      {state.trial && !state.cycle.cancelAtPeriodEnd && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm font-medium text-emerald-900">
            You're on a 7-day free trial — full access, nothing charged yet.
          </p>
          <p className="text-xs text-emerald-800 mt-1">
            {state.trial.endsAt ? (
              <>Your first payment{state.tier ? ` of ${formatGBP(state.tier.monthlyPricePence)}` : ''} is on{' '}
              <span className="font-semibold">{new Date(state.trial.endsAt).toLocaleDateString('en-GB')}</span>, when
              your 12-month term begins. Cancel any time before then at no cost.</>
            ) : (
              <>Your first payment is taken when the trial ends, when your 12-month term begins. Cancel any time before then at no cost.</>
            )}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <button
              onClick={handleActivateNow}
              disabled={actionLoading}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              Start paid plan now
            </button>
            <button
              onClick={handleCancelTrial}
              disabled={actionLoading}
              className="text-sm text-red-600 hover:text-red-800 hover:underline disabled:opacity-50"
            >
              Cancel trial — no charge
            </button>
          </div>
        </div>
      )}

      {/* Trial cancelled but still running: access continues to trial end,
          nothing will be charged, and they can change their mind. */}
      {state.trial && state.cycle.cancelAtPeriodEnd && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm font-medium text-orange-900">
            Your trial is cancelled — nothing will be charged.
          </p>
          <p className="text-xs text-orange-800 mt-1">
            You keep full access until{' '}
            <span className="font-semibold">
              {state.trial.endsAt ? new Date(state.trial.endsAt).toLocaleDateString('en-GB') : 'the end of your trial'}
            </span>, then your account closes. Changed your mind?
          </p>
          <button
            onClick={handleResumeTrial}
            disabled={actionLoading}
            className="mt-3 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            Resume trial
          </button>
        </div>
      )}

      {state.source === 'grandfathered' && state.legacyPlan?.type === 'monitoring' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          You're on a legacy plan with {formatNumber(state.totalQuota.monitoring)} monitoring checks per month.
          You can keep this plan as long as you like, or switch to one of our new tiers below.
        </div>
      )}

      {state.source === 'grandfathered' && state.legacyPlan?.type === 'pro' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          You're on the legacy MarketMapr Pro plan (£100/mo). It doesn't include monitoring or job-vacancy
          tracking — switch to one of our new tiers below to unlock those.
        </div>
      )}

      {state.source === 'grandfathered' && state.legacyPlan?.type === 'access' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          You have grandfathered access to MarketMapr. Reach out to support if you'd like to enable
          monitoring or job-vacancy tracking.
        </div>
      )}

      {/* Starter's free month of Claude: start it here or in Integrations */}
      {state.tier?.key === 'starter' && connector && (
        connector.connectorOffer ? (
          <ConnectorMonthCard token={token} info={connector} onStarted={loadConnector} compact />
        ) : connector.connectorMonth && connector.connectorUntil ? (
          <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-900">
            Your free month of Claude is running until <strong>{new Date(connector.connectorUntil).toLocaleDateString('en-GB')}</strong>.
            Set it up in <a href="/settings?tab=integrations" className="underline font-medium">Settings → Integrations</a>. On Growth and Scale, Claude is part of the plan.
          </div>
        ) : connector.connectorUntil ? (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
            Your free month of Claude ended on <strong>{new Date(connector.connectorUntil).toLocaleDateString('en-GB')}</strong>. It's included on Growth and Scale — choose a plan below to carry on.
          </div>
        ) : null
      )}

      {/* Usage bars — only show when there's an actual allowance to track */}
      {state.totalQuota.monitoring > 0 && (
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">Monitoring checks</span>
              <span className="text-gray-600">
                {formatNumber(state.cycle.used)} / {formatNumber(state.totalQuota.monitoring)} used
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${monitoringPct >= 90 ? 'bg-red-500' : monitoringPct >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                style={{ width: `${monitoringPct}%` }}
              />
            </div>
          </div>

          {state.seats && state.seats.maxSeats != null && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700">Users</span>
                <span className="text-gray-600">
                  {state.seats.used} of {state.seats.maxSeats} used
                  {state.seats.pending > 0 ? ` · ${state.seats.pending} invite${state.seats.pending === 1 ? '' : 's'} pending` : ''}
                </span>
              </div>
              {state.seats.used + state.seats.pending >= state.seats.maxSeats && state.seats.upgradeTo && (
                <p className="text-xs text-amber-700">All seats in use — upgrade to {state.seats.upgradeTo} to add more people.</p>
              )}
            </div>
          )}

          {state.totalQuota.jobs > 0 && (
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700">Tracked job vacancies</span>
                <span className="text-gray-600">
                  Up to {formatNumber(state.totalQuota.jobs)} per month
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active bolt-ons */}
      {state.activeBoltOns.length > 0 && (
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-3">Active add-ons</h3>
          <div className="space-y-2">
            {state.activeBoltOns.map((b, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <div>
                  <span className="font-medium text-gray-900">
                    +{formatNumber(b.quantity * b.quotaPerUnit)} {b.type === 'monitoring' ? 'monitoring checks' : 'tracked jobs'}
                  </span>
                  {b.quantity > 1 && <span className="text-sm text-gray-500 ml-2">× {b.quantity}</span>}
                  {b.scheduledRemovalAt && (
                    <span className="ml-3 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                      ends {new Date(b.scheduledRemovalAt).toLocaleDateString('en-GB')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {formatGBP(b.quantity * b.unitPricePence)}/mo
                  </span>
                  {!b.scheduledRemovalAt && b.id != null && (
                    <button
                      onClick={() => handleScheduleRemoval(b.id)}
                      disabled={actionLoading}
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Remove at period end
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add an add-on (only for active tier subscriptions) */}
      {state.source === 'tiered' && (state.boltOnRates.monitoring || state.boltOnRates.jobs) && (
        <div className="border-t pt-6">
          <h3 className="font-medium text-gray-900 mb-1">Add an add-on</h3>
          <p className="text-sm text-gray-600 mb-4">
            {state.enterprise
              ? 'Click an option to review and confirm. Each add-on is billed monthly to your card at your enterprise rate until you remove it.'
              : "Click an option to review and confirm. You'll be charged a prorated amount immediately for the rest of your current billing period, then the full monthly price from your next renewal."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {state.boltOnRates.monitoring && (
              <button
                onClick={() => handleAddBoltOn('monitoring')}
                disabled={actionLoading}
                className="flex items-center justify-between rounded-lg border border-gray-300 hover:border-blue-500 hover:bg-blue-50 px-4 py-3 text-left disabled:opacity-50"
              >
                <div>
                  <div className="font-medium text-gray-900">+ Monitoring</div>
                  <div className="text-xs text-gray-600">+{formatNumber(state.boltOnRates.monitoring.quotaPerUnit)} checks/mo</div>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {formatGBP(state.boltOnRates.monitoring.unitPricePence)}/mo
                </span>
              </button>
            )}
            {state.boltOnRates.jobs && (
              <button
                onClick={() => handleAddBoltOn('jobs')}
                disabled={actionLoading}
                className="flex items-center justify-between rounded-lg border border-gray-300 hover:border-blue-500 hover:bg-blue-50 px-4 py-3 text-left disabled:opacity-50"
              >
                <div>
                  <div className="font-medium text-gray-900">+ Job Vacancies</div>
                  <div className="text-xs text-gray-600">+{formatNumber(state.boltOnRates.jobs.quotaPerUnit)} jobs/mo</div>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {formatGBP(state.boltOnRates.jobs.unitPricePence)}/mo
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cycle dates */}
      {state.cycle.periodEnd && state.source !== 'none' && (
        <div className="text-xs text-gray-500">
          Current period: {state.cycle.periodStart && new Date(state.cycle.periodStart).toLocaleDateString('en-GB')} – {new Date(state.cycle.periodEnd).toLocaleDateString('en-GB')}
        </div>
      )}

      {/* Cancellation-pending banner — fires for both new tier subscribers
          and legacy / grandfathered users who've cancelled their legacy
          Stripe sub. Both expose cancelAtPeriodEnd + periodEnd via
          getUserSubscriptionState. */}
      {state.cycle.cancelAtPeriodEnd && state.cycle.periodEnd && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-900">
          Your subscription is scheduled to end on{' '}
          <strong>{new Date(state.cycle.periodEnd).toLocaleDateString('en-GB')}</strong>. You'll keep
          full access until then. Re-subscribe before that date to keep your plan.
        </div>
      )}

      {/* Stripe portal for grandfathered users */}
      {state.source === 'grandfathered' && (
        <div className="border-t pt-6">
          <button
            onClick={handleManageLegacy}
            disabled={actionLoading}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            {actionLoading ? 'Opening…' : 'Manage payment method →'}
          </button>
        </div>
      )}

      {/* Tier comparison — not for enterprise accounts (plan set by agreement) */}
      <div className={`border-t pt-6 ${state.enterprise ? 'hidden' : ''}`}>
        <h3 className="font-medium text-gray-900 mb-1">Choose a plan</h3>
        {tierNotice && (
          <div className={`my-3 p-3 rounded-lg text-sm border ${tierNotice.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-800'}`}>
            {tierNotice.msg}
          </div>
        )}
        {actionLoading && <div className="my-2 text-sm text-gray-500">Updating your plan…</div>}
        <p className="text-sm text-gray-600 mb-4">
          All plans include monitoring and job-vacancy tracking. Add-ons available on every tier.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiers.map(tier => {
            const isCurrent = state.tier?.key === tier.key;
            return (
              <div
                key={tier.id}
                className={`bg-white rounded-lg border-2 p-5 flex flex-col ${
                  isCurrent ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">{tier.display_name}</h4>
                  {isCurrent && (
                    <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded">Current</span>
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-gray-900">{formatGBP(tier.monthly_price_pence)}</span>
                  <span className="text-gray-500">/mo</span>
                </div>
                <ul className="space-y-2 text-sm text-gray-700 mb-5 flex-1">
                  {tier.max_seats !== undefined && (
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span className="font-medium">{seatRange(tiers, tier)}</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{formatNumber(tier.monthly_monitoring_checks)} monitoring checks/mo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>Up to {formatNumber(tier.monthly_job_vacancies)} tracked jobs/mo</span>
                  </li>
                  {tier.monthly_contact_credits > 0 && (
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>{formatNumber(tier.monthly_contact_credits)} contact data credits/mo</span>
                    </li>
                  )}
                  {tier.includes_ada && (
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span>Ada AI scoring included</span>
                    </li>
                  )}
                  {tier.bolt_ons.monitoring && (
                    <li className="flex items-start gap-2 text-gray-500">
                      <span className="text-gray-400">+</span>
                      <span>Add {formatNumber(tier.bolt_ons.monitoring.quota_per_unit)} monitoring at {formatGBP(tier.bolt_ons.monitoring.unit_price_pence)}/mo</span>
                    </li>
                  )}
                  {tier.bolt_ons.jobs && (
                    <li className="flex items-start gap-2 text-gray-500">
                      <span className="text-gray-400">+</span>
                      <span>Add {formatNumber(tier.bolt_ons.jobs.quota_per_unit)} jobs at {formatGBP(tier.bolt_ons.jobs.unit_price_pence)}/mo</span>
                    </li>
                  )}
                </ul>
                {(() => {
                  // Three button states:
                  // - User already on this exact tier → disabled "Current Plan"
                  // - User on a DIFFERENT tier → disabled (modify-subscription
                  //   flow not yet built; coming next)
                  // - User has no tier yet (new or grandfathered) → live "Choose Plan"
                  if (isCurrent) {
                    return (
                      <button
                        disabled
                        className="w-full px-4 py-2 rounded-lg font-medium bg-gray-100 text-gray-500 cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    );
                  }
                  if (state.source === 'tiered') {
                    const isUpgrade = state.tier
                      ? tier.monthly_price_pence > state.tier.monthlyPricePence
                      : false;
                    return (
                      <button
                        onClick={() => handleSwitchTier(tier.key)}
                        disabled={actionLoading || state.cycle.cancelAtPeriodEnd}
                        title={state.cycle.cancelAtPeriodEnd ? 'Cancel pending — re-subscribe first to switch tier' : ''}
                        className={`w-full px-4 py-2 rounded-lg font-medium ${
                          isUpgrade
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isUpgrade ? `Upgrade to ${tier.display_name}` : `Downgrade to ${tier.display_name}`}
                      </button>
                    );
                  }
                  return (
                    <button
                      onClick={() => handleChoosePlan(tier.key)}
                      disabled={actionLoading}
                      className="w-full px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Opening checkout…' : 'Choose Plan'}
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancel subscription — shown for:
          - active tier subscribers who haven't already scheduled cancellation
          - grandfathered users on a paid legacy plan (Pro £100/mo or legacy
            monitoring) who haven't already scheduled cancellation. The
            "access" grandfathered type has no Stripe subscription to
            cancel, so we hide the button there. */}
      {/* Minimum-term commitment: while active, the cancel action is
          replaced by a note showing the earliest date they can cancel. The
          server also enforces this (POST /api/subscription/cancel returns
          409 minimum_term_active), so this is UX, not the enforcement. */}
      {!state.trial && !state.cycle.cancelAtPeriodEnd && state.commitment?.active && (
        <div className="border-t pt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900 font-medium">
              You're on a {state.commitment.termMonths}-month minimum term.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              You can cancel from{' '}
              <span className="font-semibold">
                {new Date(state.commitment.end).toLocaleDateString('en-GB')}
              </span>
              . To discuss cancelling earlier, contact{' '}
              <a href="mailto:alex@marketmapr.com" className="underline">alex@marketmapr.com</a>{' '}
              — an early-termination fee for the remaining months applies.
            </p>
          </div>
        </div>
      )}

      {!state.enterprise && !state.trial && !state.cycle.cancelAtPeriodEnd && !state.commitment?.active &&
        ((state.source === 'tiered') ||
         (state.source === 'grandfathered' &&
           (state.legacyPlan?.type === 'pro' || state.legacyPlan?.type === 'monitoring'))) && (
        <div className="border-t pt-6">
          <button
            onClick={handleCancel}
            disabled={actionLoading}
            className="text-sm text-red-600 hover:text-red-800 hover:underline disabled:opacity-50"
          >
            Cancel subscription
          </button>
          <p className="text-xs text-gray-500 mt-1">
            You'll keep full access until the end of your current billing period.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPanel;
